import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GRADIENTS, COLORS } from '../../Website/themeConstants';

export default function Achievements() {
  const { ref: sectionRef, inView: isInView } = useInView({ threshold: 0.3 });
  const [animateFloats, setAnimateFloats] = useState(false);

  useEffect(() => {
    setAnimateFloats(isInView);
  }, [isInView]);

  const AwardCard = ({ award, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className={`rounded-xl ${COLORS.background.glass} p-6 shadow-lg backdrop-blur-sm border ${COLORS.border.glass} hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
    >
      <div className="flex items-start mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 bg-gradient-to-br ${GRADIENTS.background.accent}`}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        <div>
          <h3 className={`text-xl font-bold bg-gradient-to-r ${GRADIENTS.text.accent} bg-clip-text text-transparent`}>
            {award.title}
          </h3>
          <p className={`text-sm font-medium ${COLORS.text.secondary}`}>{award.name}</p>
        </div>
      </div>
      {award.date && (
        <p className={`text-xs mb-3 ${COLORS.text.secondary}`}>
          {award.date}
        </p>
      )}
      <p className={`text-base ${COLORS.text.primary}`}>
        {award.description}
      </p>
    </motion.div>
  );

  const awards = [
    {
      name: 'AMC Group of Educational Institutions, Manissery',
      title: 'Performance Appreciation Award – NAAC 2022',
      date: 'April 26, 2022',
      description: 'Honored for exceptional contributions in IT Support during NAAC 2022, including data structuring coordination and streamlining of digital management systems.',
    },
    {
      name: 'AMC Group of Educational Institutions, Manissery',
      title: 'Best Student Entrepreneur – Merit Day 2023',
      date: 'October 4, 2023',
      description: 'Recognized as Best Student Entrepreneur during Merit Day for the M.Com Finance (2021–2023) batch, celebrating innovation and academic excellence.',
    },
    {
      name: 'Calicut University',
      title: 'Best Student Startup Award 2024',
      date: 'January 16, 2024',
      description: 'Received the Excellence in Innovation and Leadership Award from Shri. P. A. Mohammed Riyas, Minister for Public Works and Tourism, Government of Kerala.',
    },
    {
      name: 'Pattambi Municipality',
      title: 'MLA Excellence Award',
      date: 'June 21, 2024',
      description: 'Presented by Shri. K. Krishnankutty (M. Ele.), Minister for Electricity, Government of Kerala for leadership and innovation impacting the community.',
    },
    {
      name: 'Deshabhimani | Kaumudi | Malayala Manorama',
      title: 'Media Recognition – First University Startup',
      date: '2024',
      description: 'Necttos became the first officially recognized startup under the University of Calicut, featured across major newspapers.',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen py-24 px-6 md:px-16 overflow-hidden"
      style={{
                background: 'linear-gradient(135deg, #041c38 100%,#031223 0%)',
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-cyan-500 opacity-10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-purple-600 opacity-10 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
            ACHIEVEMENTS
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        {/* Awards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {awards.map((award, index) => (
            <AwardCard key={index} award={award} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}